import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Legend } from 'recharts';
import { YearlyCalculation } from '@/types/businesscase';
import { Info, Download } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { useChartExport } from '@/hooks/useChartExport';

interface CostsVsRevenuesChartProps {
  data: YearlyCalculation[];
  businesscaseName?: string;
}

const chartConfig = {
  revenue: {
    label: 'Opbrengsten',
    color: 'hsl(142, 76%, 36%)', // green
  },
  opex: {
    label: 'Kosten (OPEX)',
    color: 'hsl(0, 84%, 60%)', // red
  },
};

export function CostsVsRevenuesChart({ data, businesscaseName }: CostsVsRevenuesChartProps) {
  const { exportToPNG } = useChartExport();
  
  const formatCurrency = (value: number) => {
    return `€${(value / 1000).toFixed(0)}k`;
  };

  return (
    <Card className="border-border" id="costs-chart">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              Kosten vs Opbrengsten
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="font-semibold">Jaarlijkse Vergelijking</p>
                    <p className="text-xs mt-1">
                      <strong>Opbrengsten:</strong> Netto opbrengsten (na aftrek kosten)
                    </p>
                    <p className="text-xs mt-1">
                      <strong>Kosten:</strong> |EMS Costs| + |Total OPEX|
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </CardTitle>
            <CardDescription>
              Vergelijking van jaarlijkse operationele kosten en opbrengsten
            </CardDescription>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => exportToPNG('chart-costs-revenues', 'kosten-vs-opbrengsten', businesscaseName)}
          >
            <Download className="w-4 h-4 mr-2" />
            PNG
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="h-[400px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis 
                dataKey="year" 
                stroke="hsl(var(--muted-foreground))"
                style={{ fontSize: '12px' }}
              />
              <YAxis 
                stroke="hsl(var(--muted-foreground))"
                tickFormatter={formatCurrency}
                style={{ fontSize: '12px' }}
              />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Legend />
              
              <Bar 
                dataKey="revenue" 
                fill={chartConfig.revenue.color}
                name={chartConfig.revenue.label}
                radius={[4, 4, 0, 0]}
              />
              <Bar 
                dataKey="opex" 
                fill={chartConfig.opex.color}
                name={chartConfig.opex.label}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
